import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2 } from 'lucide-react';
// eslint-disable-next-line no-restricted-imports
import { apiClient } from '../../../../../shared/api/apiClient';
import { useSendWhatsAppMessage } from '../../../../client/hooks/useWhatsAppData';

export default function WhatsTaskLinkSearch({ chatJid, onClose, onLinkComplete }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const sendMessageMutation = useSendWhatsAppMessage(chatJid);

  // Fetch all tasks for searching
  const { data: allTasks = [], isLoading } = useQuery({
    queryKey: ['all-tasks-link-search'],
    queryFn: async () => {
      const res = await apiClient.get('/api/tasks?all=true');
      return res.success && res.tasks ? res.tasks : [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const filteredTasks = React.useMemo(() => {
    if (!searchTerm) return allTasks.slice(0, 10);
    const term = searchTerm.toLowerCase();
    return allTasks
      .filter(t => t.title?.toLowerCase().includes(term) || t.id.toLowerCase().includes(term))
      .slice(0, 10);
  }, [allTasks, searchTerm]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredTasks.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const task = filteredTasks[selectedIndex];
      if (task) {
        handleLinkTask(task);
      }
    } else if (e.key === 'Escape') {
      if (onClose) onClose();
    }
  };

  const handleLinkTask = (task) => {
    if (!chatJid) return;
    const taskLink = `https://colonny.xyz/t/${task.id}`;
    
    sendMessageMutation.mutate({ text: taskLink }, {
      onSuccess: () => {
        setSearchTerm('');
        if (onLinkComplete) onLinkComplete();
        if (onClose) onClose();
      }
    });
  };

  return (
    <div ref={containerRef} style={{ marginTop: '8px', position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center', background: '#f3f4f6', 
        borderRadius: '6px', padding: '6px 10px', gap: '8px'
      }}>
        <Search size={14} color="#6b7280" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar tarea..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setSelectedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          style={{
            border: 'none', background: 'transparent', outline: 'none',
            fontSize: '13px', width: '100%', color: '#111'
          }}
        />
        {sendMessageMutation.isPending && <Loader2 size={14} className="spin" color="#6b7280" />}
      </div>

      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginTop: '4px',
        maxHeight: '250px', overflowY: 'auto', zIndex: 10
      }}>
        {isLoading ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>Cargando...</div>
        ) : filteredTasks.length === 0 ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>No se encontraron tareas.</div>
        ) : (
          filteredTasks.map((task, index) => {
            const assignee = task.assignee || {};
            const avatarUrl = assignee.avatar_url;
            const fallbackName = (assignee.name || assignee.nick || 'U').charAt(0).toUpperCase();

            return (
              <div
                key={task.id}
                onClick={() => handleLinkTask(task)}
                onMouseEnter={() => setSelectedIndex(index)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 12px', cursor: 'pointer',
                  background: index === selectedIndex ? '#f3f4f6' : 'transparent',
                  borderBottom: '1px solid #f9fafb'
                }}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e5e7eb', color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                    {fallbackName}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {task.title}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
